import React, { MouseEvent, useContext, useEffect, useState } from 'react';
import Menu from '@mui/material/Menu';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Link } from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import moment from 'moment';
import Badge from '@mui/material/Badge';
import AvatarGroup from '@mui/material/AvatarGroup';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { useApolloClient } from '@apollo/client';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Theme } from '@mui/material/styles';
import Context from '../../context';
import useRoutes from '../../components/_hook/useRoutes';
import { useChatroomQuery } from '../../../graphql/query/chatroom/useChatroomQuery';
import SchemaCompanyLogoAvatar from '../../components/_schema/SchemaCompanyLogoAvatar';
import SchemaUserAvatar from '../../components/_schema/SchemaUserAvatar';
import Moment from '../../components/Moment';
import { IChatroomSchema } from '../../../graphql/type/chatroom';
import HeaderNotificationMessageCreatedSubscription from './_module/HeaderNotificationMessageCreatedSubscription';
import HeaderNotificationCompanyNotificationCreatedSubscription from './_module/HeaderNotificationCompanyNotificationCreatedSubscription';
import { companyNotificationQuery } from '../../../graphql/query/companyNotification/useCompanyNotificationQuery';
import { ICompanyNotificationSchema } from '../../../graphql/type/companyNotification';
import { IPagination } from '../../../graphql/query/_module/baseQueryType';
import COMPANY_NOTIFICATION from '../../components/constant/companyNotification';
import CompanyNotificationIncomingNewOrderMenuItem from './_module/CompanyNotificationIncomingNewOrderMenuItem';
import ButtonSubmit from '../../components/_button/ButtonSubmit';
import CompanyNotificationIncomingNewOrderAskForQuoteMenuItem from './_module/CompanyNotificationIncomingNewOrderAskForQuoteMenuItem';
import { useMarkAllReadCompanyNotificationsMutation } from '../../../graphql/mutation/companyNotification/markAllReadCompanyNotificationsMutation';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    borderRadius: '50px',
  },
  buttonSubtitle: {
    fontSize: '12px',
  },
  iconArrowUp: {
    color: '#1D7EA1',
    fontSize: '24px !important',
  },
  iconArrowDown: {
    fontSize: '24px !important',
  },
  menuItem: {
    height: '48px',
  },
  menuMessageItem: {
    padding: '8px',
  },
  mainButton: {
    borderRadius: '50px',
  },
  menuPaper: {
    width: '360px',
    maxHeight: '480px',
  },
  menuList: {
    padding: '0px 0px',
  },
  menuItemText: {
    color: '#f00',
  },
  notificationUnreadIcon: {
    '&::after': {
      position: 'absolute',
      top: '10px',
      right: '10px',
      content: "''",
      width: '6px',
      height: '6px',
      backgroundColor: '#f92949',
      borderRadius: '50%',
    },
  },
  tabs: {
    top: 0,
    position: 'sticky',
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#2f2f2f',
    zIndex: 1,
  },
}));

interface IProps {}

export default function HeaderNotificationButton(props: IProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { company } = useContext(Context);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [tab, setTab] = useState(1);
  const [chatrooms, setChatrooms] = useState<IChatroomSchema[]>([]);
  const [companyNotifications, setCompanyNotifications] = useState<
    ICompanyNotificationSchema[]
  >([]);
  const [
    companyNotificationOffset,
    setCompanyNotificationOffset,
  ] = useState<number>(0);
  const [
    companyNotificationPagination,
    setCompanyNotificationPagination,
  ] = useState<IPagination | null>(null);
  const { path } = useRoutes();
  const {
    company: contextCompany,
    user: contextUser,
    isSuperAdmin,
  } = useContext(Context);

  const [
    markAllReadCompanyNotificationsMutation,
  ] = useMarkAllReadCompanyNotificationsMutation('isSuccess', {
    onCompleted: (data) => {
      setCompanyNotifications(
        companyNotifications.map((companyNotification) => {
          const tempCompanyNotification = { ...companyNotification };
          tempCompanyNotification.isRead = true;
          return tempCompanyNotification;
        })
      );
    },
  });

  const companyNotificationFragment = `_id 
			companyId
			type
			description
			data
			isRead
			created {
				time
			}`;

  async function getCompanyNotificationData(isNewCompany?: boolean) {
    const response = await client.query({
      query: companyNotificationQuery(
        companyNotificationFragment,
        undefined,
        true
      ),
      variables: {
        filter: { companyId: company._id },
        sort: { createdTime: -1 },
        limit: 10,
        skip: isNewCompany ? 0 : companyNotificationOffset,
      },
    });
    if (
      response &&
      response.data.companyNotifications &&
      response.data.companyNotifications.items
    ) {
      if (isNewCompany) {
        setCompanyNotifications(response.data.companyNotifications.items);
      } else {
        setCompanyNotifications([
          ...companyNotifications,
          ...response.data.companyNotifications.items,
        ]);
      }

      setCompanyNotificationPagination(
        response.data.companyNotifications.pagination
      );
    }
  }

  useEffect(() => {
    getCompanyNotificationData();
  }, [companyNotificationOffset]);
  useEffect(() => {
    setCompanyNotifications([]);
    setCompanyNotificationOffset(0);
    getCompanyNotificationData(true);
  }, [company._id]);
  useEffect(() => {
    getCompanyNotificationData(true);
  }, []);

  const chatroomFragment = `
			_id
            users {
              id
              userData {
                 _id 
                 profile {
                  name {
                    firstname
                    lastname
                  }
                 }
              }
              from
              companyData {
                _id
                name {
                    la
                    text
                }
                origin
                slug
              }
            }
            seen {
              id
              from
              lastseen
            }
            lastmessage {
              text
              created {
                time
              }
            }
		`;

  const { data: chatroomData, loading: chatroomLoading } = useChatroomQuery(
    chatroomFragment,
    {
      variables: {
        filter: {
          companyId: contextCompany._id,
        },
        sort: {
          lastMessageCreatedTime: -1,
        },
        limit: 5,
      },
    }
  );

  useEffect(() => {
    setChatrooms([]);
    if (
      chatroomData &&
      chatroomData.chatrooms &&
      chatroomData.chatrooms.items
    ) {
      const newChatrooms = chatroomData.chatrooms.items.filter((item) => {
        const seen = item.seen || [];
        const lastMessage = item.lastmessage;

        if (!lastMessage) return false;

        const me =
          seen.find((i) => i.from === contextCompany._id) ||
          seen.find((i) => i.id === contextUser._id);

        if (
          me &&
          me.lastseen &&
          lastMessage &&
          lastMessage.created &&
          lastMessage.created.time
        ) {
          return !moment(me.lastseen).isAfter(lastMessage.created.time);
        }

        return false;
      });
      setChatrooms(newChatrooms);
    }
  }, [JSON.stringify(chatroomData)]);

  const isMenuOpen = Boolean(anchorEl);
  const handleMenuOpen = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    markAllReadCompanyNotificationsMutation({
      variables: {
        companyId: contextCompany._id,
      },
    });
  };

  const menuId = 'notification-menu';
  const unreadNotificationCount = companyNotifications.filter(
    (notification) => !notification.isRead
  ).length;

  const renderMenu = (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        id={menuId}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={isMenuOpen}
        classes={{
          paper: classes.menuPaper,
          list: classes.menuList,
        }}
        onClose={handleMenuClose}
      >
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label={`${t('notification', {
              count: companyNotifications.length,
            })}${
              unreadNotificationCount
                ? ` (${unreadNotificationCount}${
                    unreadNotificationCount !== 0 &&
                    unreadNotificationCount % 10 === 0
                      ? '+'
                      : ''
                  })`
                : ''
            }`}
            value={1}
          />
          <Tab
            label={`${t('unread_message')}${
              chatrooms.length ? ` (${chatrooms.length})` : ''
            }`}
            value={2}
          />
        </Tabs>
        <Divider />
        {tab === 1 && (
          <>
            {companyNotifications.map((companyNotification) => (
              <React.Fragment key={companyNotification._id}>
                {companyNotification.type ===
                  COMPANY_NOTIFICATION.TYPE.INCOMING_NEW_ORDER && (
                  <CompanyNotificationIncomingNewOrderMenuItem
                    companyNotification={companyNotification}
                  />
                )}
                {companyNotification.type ===
                  COMPANY_NOTIFICATION.TYPE
                    .INCOMING_NEW_ORDER_ASK_FOR_QUOTE && (
                  <CompanyNotificationIncomingNewOrderAskForQuoteMenuItem
                    companyNotification={companyNotification}
                  />
                )}
                <Divider />
              </React.Fragment>
            ))}
            {companyNotifications.length === 0 && (
              <MenuItem disabled>
                <ListItemText
                  primary={`0 ${t('notification')}`}
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                  }}
                />
              </MenuItem>
            )}
            {companyNotificationPagination &&
              companyNotificationPagination.hasNextPage && (
                <MenuItem disableGutters>
                  <ButtonSubmit
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setCompanyNotificationOffset(
                        companyNotificationOffset + 10
                      );
                    }}
                  >
                    {t('load_more')}
                  </ButtonSubmit>
                </MenuItem>
              )}
          </>
        )}
        {tab === 2 && (
          <>
            {chatrooms.map((chatroomItem) => (
              <React.Fragment key={chatroomItem._id}>
                <MenuItem
                  className={classes.menuMessageItem}
                  component={Link}
                  to={path('messenger.chatroom', {
                    companyID: company._id,
                    chatroomID: chatroomItem._id,
                  })}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <ListItemAvatar>
                        <AvatarGroup max={6}>
                          {chatroomItem.users.map((user) => (
                            <React.Fragment key={user.id}>
                              {user.userData && user.companyData ? (
                                <Badge
                                  overlap="circular"
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                  }}
                                  badgeContent={
                                    <SchemaCompanyLogoAvatar
                                      badge
                                      company={user.companyData}
                                    />
                                  }
                                >
                                  <SchemaUserAvatar user={user.userData} />
                                </Badge>
                              ) : (
                                <>
                                  {user.userData && (
                                    <SchemaUserAvatar user={user.userData} />
                                  )}
                                  {user.companyData && (
                                    <SchemaCompanyLogoAvatar
                                      company={user.companyData}
                                    />
                                  )}
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </AvatarGroup>
                      </ListItemAvatar>
                    </Grid>
                    <Grid item>
                      {chatroomItem.lastmessage &&
                        chatroomItem.lastmessage.text && (
                          <ListItemText
                            primary={
                              chatroomItem.lastmessage.text.length > 100
                                ? `${chatroomItem.lastmessage.text.substring(
                                    0,
                                    100
                                  )}...`
                                : chatroomItem.lastmessage.text
                            }
                            secondary={
                              <>
                                {chatroomItem.lastmessage.created &&
                                  chatroomItem.lastmessage.created.time && (
                                    <Moment
                                      time={
                                        chatroomItem.lastmessage.created.time
                                      }
                                    />
                                  )}
                              </>
                            }
                            primaryTypographyProps={{
                              variant: 'subtitle2',
                              style: {
                                whiteSpace: 'pre-wrap',
                              },
                            }}
                            secondaryTypographyProps={{}}
                          />
                        )}
                    </Grid>
                  </Grid>
                </MenuItem>
                <Divider />
              </React.Fragment>
            ))}
            {chatrooms.length === 0 && (
              <MenuItem disabled>
                <ListItemText
                  primary={`0 ${t('unread_message')}`}
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                  }}
                />
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );

  const renderDesktopIconButton = (
    <IconButton
      className={classNames(
        classes.mainButton,
        {
          [classes.notificationUnreadIcon]:
            unreadNotificationCount || chatrooms.length,
        },
        'default-layout-header-notification-button-joyride'
      )}
      color="inherit"
      onClick={handleMenuOpen}
      size="large"
    >
      <NotificationsIcon />
    </IconButton>
  );

  return (
    <>
      <HeaderNotificationMessageCreatedSubscription
        chatroomFragment={chatroomFragment}
        variables={{
          userId: contextUser._id as string,
          companyId: contextCompany._id as string,
        }}
        onSubscriptionData={(subscriptionData) => {
          if (subscriptionData && subscriptionData.chatroom) {
            const messageChatroom = subscriptionData.chatroom;
            const messageChatroomUsers = messageChatroom.users || [];

            if (
              messageChatroomUsers.find((u) => u.from === contextCompany._id)
            ) {
              let newChatrooms = chatrooms.filter(
                (chatroom) => chatroom._id !== messageChatroom._id
              );
              newChatrooms = [...[messageChatroom], ...newChatrooms];

              setChatrooms(newChatrooms);
            }
          }
        }}
      />
      <HeaderNotificationCompanyNotificationCreatedSubscription
        companyNotificationFragment={companyNotificationFragment}
        variables={{
          companyId: contextCompany._id as string,
        }}
        onSubscriptionData={(subscriptionData) => {
          if (subscriptionData && subscriptionData.createdCompanyNotification) {
            const newCompanyNotification =
              subscriptionData.createdCompanyNotification;
            setCompanyNotifications([
              newCompanyNotification,
              ...companyNotifications,
            ]);
          }
        }}
      />
      {renderDesktopIconButton}
      {renderMenu}
    </>
  );
}
